import { useState } from 'react';
import { useSelector } from 'react-redux';
import { kebabCase } from 'lodash';
import { selectIsSmsVerificationFeatureFlagOn } from 'core/userContext';
import { SERVICE_PROVIDERS, SmsEspAccount, SMS_PROVIDERS } from 'modules/graphql/types';
import { BUTTON_SIZES } from 'ui/button';
import { Add24 } from 'ui/icons';
import {
  ARROW_DIRECTION_DOWN,
  CollapsibleContent,
  CollapsibleContentArrow,
} from 'ui/collapsibleContent';
import {
  SMS_SENDER_TERMS_BY_SERVICE_PROVIDER,
  SMS_VERIFICATION_PROFILE_TERM,
} from '../../../constants';
import { useModalsContext, useSendingPlatformsQueryContext } from '../../../contexts';
import { MergedSmsSender } from '../../../types';
import { composeSmsAccountDetailsText, pluralResourcesText } from '../../../utils';
import {
  ActionButton,
  Wrapper,
  Main,
  AccountName,
  AccountDetails,
  LeftContent,
  RightContent,
  SectionHeading,
  ArrowButton,
} from '../AccountsSection.styled';
import { SmsOverflowMenu } from './SmsOverflowMenu';
import { SmsSenderRow } from './SmsSenderRow';
import { VerificationProfileRow } from './VerificationProfiles/VerificationProfileRow';

type Props = {
  espAccount: SmsEspAccount;
};

export const SmsAccount = ({ espAccount }: Props) => {
  const { id, esp, name } = espAccount;

  const { showAddSmsSenderModal, showAddVerificationProfileModal } = useModalsContext();
  const { selectSmsSenders, selectVerificationProfilesForEspAccount } =
    useSendingPlatformsQueryContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const senders = selectSmsSenders(id);
  const senderTerm = SMS_SENDER_TERMS_BY_SERVICE_PROVIDER[esp as SMS_PROVIDERS];
  const verificationProfiles = selectVerificationProfilesForEspAccount(id);
  const isAddingVerificationEnabled = useSelector(selectIsSmsVerificationFeatureFlagOn);

  return (
    <Wrapper data-test={`SMS-account-${kebabCase(name)}`}>
      <Main>
        <LeftContent>
          <AccountName>{name}</AccountName>
          <AccountDetails>{composeSmsAccountDetailsText(espAccount)}</AccountDetails>
        </LeftContent>
        <RightContent>
          {senders.length > 0 ? (
            <ArrowButton onClick={() => setIsExpanded(!isExpanded)} size={BUTTON_SIZES.SMALL}>
              <span>{pluralResourcesText(senders.length, senderTerm)}</span>
              <CollapsibleContentArrow
                isCollapsed={!isExpanded}
                arrowStart={ARROW_DIRECTION_DOWN}
                arrowEnd={180}
                width={16}
                height={16}
              />
            </ArrowButton>
          ) : (
            <ActionButton
              size={BUTTON_SIZES.LARGE}
              onClick={() => showAddSmsSenderModal(espAccount.id)}
            >
              <Add24 />
              <span>{`Add ${senderTerm}`}</span>
            </ActionButton>
          )}
          {verificationProfiles.length > 0 ? (
            <ArrowButton onClick={() => setIsExpanded(!isExpanded)} size={BUTTON_SIZES.SMALL}>
              <span>
                {pluralResourcesText(verificationProfiles.length, SMS_VERIFICATION_PROFILE_TERM)}
              </span>
              <CollapsibleContentArrow
                isCollapsed={!isExpanded}
                arrowStart={ARROW_DIRECTION_DOWN}
                arrowEnd={180}
                width={16}
                height={16}
              />
            </ArrowButton>
          ) : (
            isAddingVerificationEnabled &&
            esp === SERVICE_PROVIDERS.TELNYX && (
              <ActionButton
                size={BUTTON_SIZES.LARGE}
                onClick={() => showAddVerificationProfileModal(espAccount.id)}
              >
                <Add24 />
                <span>{`Add ${SMS_VERIFICATION_PROFILE_TERM}`}</span>
              </ActionButton>
            )
          )}
          <SmsOverflowMenu espAccountId={id} />
        </RightContent>
      </Main>
      {senders.length > 0 && (
        <CollapsibleContent isCollapsed={!isExpanded}>
          <SectionHeading>{`${senderTerm}s`}</SectionHeading>
          {senders.map((sender) => (
            <SmsSenderRow key={sender.id} smsSender={sender as MergedSmsSender} />
          ))}
        </CollapsibleContent>
      )}
      {verificationProfiles.length > 0 && (
        <CollapsibleContent isCollapsed={!isExpanded}>
          <SectionHeading>{`${SMS_VERIFICATION_PROFILE_TERM}s`}</SectionHeading>
          {verificationProfiles.map((verificationProfile) => (
            <VerificationProfileRow
              key={verificationProfile.id}
              verificationProfile={verificationProfile}
            />
          ))}
        </CollapsibleContent>
      )}
    </Wrapper>
  );
};
